import React, { useState, useEffect } from 'react';
import { useNavigate, useSearch } from '../lib/routerCompat';
import type { RouteSearch } from '../lib/routes';
import { useToast } from '../ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../services/db';

/**
 * Creator card shape consumed by this view. Mirrors the raw creator row
 * returned from `db.getCreators()` (snake_case `rate_num`, string arrays for
 * `platforms`/`badges`) — narrower than the app-facing `Creator` type, which
 * this view never uses.
 */
interface DiscoverCreator {
  id: number;
  name: string;
  handle: string;
  niche: string;
  tags: string[];
  emoji: string;
  /** Storage key for an uploaded avatar; null while the emoji stands in. */
  avatar_path: string | null;
  bg: string;
  platforms: string[];
  badges: string[];
  rate: string;
  rate_num: number;
  bio: string;
  pitch: string | null;
  rating: number;
  completed: number;
}

const NICHE_GROUPS = [
  { label: 'All', niches: [] },
  {
    label: 'E-Commerce',
    niches: ['Fashion', 'Streetwear', 'Skincare', 'Home Decor', 'Food Content'],
  },
  { label: 'Digital & SaaS', niches: ['Tech', 'SaaS', 'Gaming', 'Entrepreneurship'] },
  { label: 'Fitness & Wellness', niches: ['Fitness', 'Wellness'] },
  { label: 'Education', niches: ['Educational', 'Personal Branding', 'Real Estate'] },
  { label: 'Faith-Based', niches: ['Faith-Based'] },
];

const RATE_FILTERS = [
  { label: 'Any Rate', min: 0, max: Infinity },
  { label: 'Under $1K', min: 0, max: 999 },
  { label: '$1K – $2K', min: 1000, max: 2000 },
  { label: '$2K+', min: 2001, max: Infinity },
];

const PLATFORMS = ['All', 'TikTok', 'Instagram', 'YouTube'];

export default function DiscoverView(): JSX.Element {
  const navigate = useNavigate();
  const showToast = useToast();
  const { session } = useAuth();
  const search = useSearch<RouteSearch['/feed']>({ strict: false });
  const [savedOnly, setSavedOnly] = useState<boolean>(Boolean(search?.saved));
  const [query, setQuery] = useState<string>('');
  const [creators, setCreators] = useState<DiscoverCreator[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [activeGroup, setActiveGroup] = useState<number>(0);
  const [activeRate, setActiveRate] = useState<number>(0);
  const [activePlatform, setActivePlatform] = useState<number>(0);
  const [saved, setSaved] = useState<Record<number, boolean>>({});
  const [mediaUrls, setMediaUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    Promise.all([db.getCreators(), db.getSavedCreatorIds()]).then(([data, savedIds]) => {
      if (cancelled) return;
      setCreators(data as DiscoverCreator[]);
      setSaved(Object.fromEntries((savedIds as number[]).map((id) => [id, true])));
      setLoading(false);
      // One batched signing call for the whole wall of avatars rather than a
      // round trip per card; cards without media keep their emoji (PIT-71).
      db.getMediaUrls(data.map((c) => c.avatar_path))
        .then((urls) => {
          if (!cancelled) setMediaUrls(urls);
        })
        .catch(() => {
          // Unsigned avatars fall back to the emoji placeholder.
        });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleSave = (e: React.MouseEvent<HTMLButtonElement>, id: number, name: string) => {
    e.stopPropagation();
    const userId = session?.user?.id;
    if (!db.demoMode && !userId) {
      showToast('Still connecting — try again in a second');
      return;
    }
    const nowSaved = !saved[id];
    setSaved((prev) => ({ ...prev, [id]: nowSaved }));
    showToast(nowSaved ? `${name} added to vault` : 'Removed from vault');
    if (nowSaved) db.saveCreator(userId, id);
    else db.unsaveCreator(userId, id);
  };

  const group = NICHE_GROUPS[activeGroup];
  const rateFilter = RATE_FILTERS[activeRate];
  const platform = PLATFORMS[activePlatform];
  const q = query.trim().toLowerCase();
  const filtered = creators.filter((c) => {
    const matchesGroup =
      group.niches.length === 0 ||
      group.niches.includes(c.niche) ||
      (c.tags || []).some((t) => group.niches.includes(t));
    const matchesRate = c.rate_num >= rateFilter.min && c.rate_num <= rateFilter.max;
    const matchesPlatform = platform === 'All' || (c.platforms || []).includes(platform);
    const matchesSaved = !savedOnly || saved[c.id];
    const matchesQuery =
      !q ||
      ([c.name, c.handle, c.niche, c.pitch, c.bio, ...(c.tags || [])] as string[])
        .filter(Boolean)
        .some((field) => field.toLowerCase().includes(q));
    return matchesGroup && matchesRate && matchesPlatform && matchesSaved && matchesQuery;
  });

  if (loading)
    return (
      <div
        className="view"
        style={{
          textAlign: 'center',
          padding: '60px 0',
          fontFamily: 'var(--cinzel)',
          color: 'var(--text-muted)',
          letterSpacing: 3,
        }}
      >
        LOADING...
      </div>
    );

  return (
    <div className="view">
      <div className="feed-header">
        <div>
          <div className="section-header">Creator Vault</div>
          <div className="feed-count">
            {filtered.length} of {creators.length} creators shown
          </div>
        </div>
      </div>

      <div className="gold-divider">
        <span className="divider-gem">◆</span>
      </div>

      <div className="creator-search">
        <input
          type="search"
          className="form-input"
          placeholder="Search creators by name, niche, or pitch…"
          aria-label="Search creators"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      <div className="filter-row">
        {NICHE_GROUPS.map((g, i) => (
          <button
            key={g.label}
            className={`filter-chip${activeGroup === i ? ' on' : ''}`}
            onClick={() => setActiveGroup(i)}
          >
            {g.label}
          </button>
        ))}
      </div>

      <div className="filter-row" style={{ marginTop: 8 }}>
        {RATE_FILTERS.map((r, i) => (
          <button
            key={r.label}
            className={`filter-chip${activeRate === i ? ' on' : ''}`}
            onClick={() => setActiveRate(i)}
          >
            {r.label}
          </button>
        ))}
      </div>

      <div className="filter-row" style={{ marginTop: 8 }}>
        {PLATFORMS.map((p, i) => (
          <button
            key={p}
            className={`filter-chip${activePlatform === i ? ' on' : ''}`}
            onClick={() => setActivePlatform(i)}
          >
            {p}
          </button>
        ))}
        <button
          className={`filter-chip${savedOnly ? ' on' : ''}`}
          onClick={() => setSavedOnly((v) => !v)}
        >
          ♥ Saved
        </button>
      </div>

      {group.niches.length > 0 && (
        <div className="filter-row" style={{ marginTop: 8 }}>
          {group.niches.map((n) => {
            const count = creators.filter(
              (c) => c.niche === n || (c.tags || []).includes(n)
            ).length;
            return (
              <span key={n} className="niche-sub-tag">
                {n} <span className="niche-sub-count">{count}</span>
              </span>
            );
          })}
        </div>
      )}

      <div className="creator-grid">
        {filtered.map((c) => (
          <div className="creator-card" key={c.id}>
            <div className="creator-thumb" style={{ background: c.bg, overflow: 'hidden' }}>
              {/* Uploaded avatar when the creator has one, else their emoji. */}
              {c.avatar_path && mediaUrls[c.avatar_path] ? (
                <img
                  src={mediaUrls[c.avatar_path]}
                  alt={c.name}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <span style={{ fontSize: 66 }}>{c.emoji}</span>
              )}
              <div className="play-btn">▶</div>
              <button
                className={`save-btn${saved[c.id] ? ' saved' : ''}`}
                onClick={(e) => toggleSave(e, c.id, c.name)}
              >
                {saved[c.id] ? '♥' : '♡'}
              </button>
            </div>
            <div className="creator-info">
              <div className="creator-name">{c.name}</div>
              <div className="creator-handle">
                {c.handle}
                {c.platforms && c.platforms.length > 0 && (
                  <span style={{ color: 'var(--text-muted)', fontSize: 10.5, marginLeft: 8 }}>
                    {c.platforms.join(' · ')}
                  </span>
                )}
              </div>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 6,
                }}
              >
                <div>
                  <span className="niche-tag primary">{c.niche}</span>
                  {(c.tags || []).includes('Faith-Based') && (
                    <span className="niche-tag faith">Faith</span>
                  )}
                  {(c.tags || [])
                    .filter((t) => t !== 'Faith-Based')
                    .slice(0, 2)
                    .map((t) => (
                      <span className="niche-tag" key={t}>
                        {t}
                      </span>
                    ))}
                </div>
                <span
                  style={{
                    fontFamily: 'var(--cinzel)',
                    fontSize: 12,
                    color: 'var(--gold)',
                    fontWeight: 700,
                  }}
                >
                  {c.rate}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
                <span style={{ color: 'var(--gold)', fontSize: 12 }}>
                  {'★'.repeat(Math.round(c.rating))}
                </span>
                <span
                  style={{
                    fontSize: 10.5,
                    color: 'var(--text-muted)',
                    fontFamily: 'var(--cinzel)',
                  }}
                >
                  {Number(c.rating).toFixed(1)} · {c.completed} deals
                </span>
              </div>
              {c.badges && c.badges.length > 0 && (
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 10 }}>
                  {c.badges.includes('verified') && (
                    <span className="creator-badge badge-verified">✓ Verified</span>
                  )}
                  {c.badges.includes('topPerformer') && (
                    <span className="creator-badge badge-top">◆ Top Performer</span>
                  )}
                  {c.badges.includes('fastDelivery') && (
                    <span className="creator-badge badge-fast">⚡ Fast Delivery</span>
                  )}
                </div>
              )}
              <div className="creator-actions">
                <button
                  className="btn-gold"
                  onClick={() => {
                    navigate({ to: '/request' });
                    showToast(`${c.name} invited to campaign`);
                  }}
                >
                  Invite to Campaign
                </button>
                <button
                  className="btn-outline"
                  onClick={() => navigate({ to: '/creators/$id', params: { id: String(c.id) } })}
                >
                  View
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filtered.length === 0 && (
        <div
          style={{
            textAlign: 'center',
            padding: '48px 0',
            color: 'var(--text-muted)',
            fontFamily: 'var(--cinzel)',
            letterSpacing: 2,
            fontSize: 16.5,
          }}
        >
          NO CREATORS MATCH YOUR FILTERS
        </div>
      )}
    </div>
  );
}
